import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
} from 'react';
import type ReactMarkdown from 'react-markdown';
import MarkdownWithMath from './MarkdownWithMath';
import type { WorkflowStep } from './parseWorkflowSteps';
import { getWorkflowUiCopy } from './workflowUiCopy';

type WorkflowStepsCardProps = {
  steps: WorkflowStep[];
  sourceText?: string;
  markdownComponents: ComponentProps<typeof ReactMarkdown>['components'];
};

type SlimeBox = {
  left: number;
  top: number;
  width: number;
  height: number;
  stretch: boolean;
};

const CARD_SCROLL_MARGIN = 16;
const SLIME_STRETCH_MS = 170;

function getChatScrollContainer(element: HTMLElement): HTMLElement | null {
  const messages = element.closest('.aida-chat__messages');
  return messages instanceof HTMLElement ? messages : null;
}

function measureDotBox(dot: HTMLElement, track: HTMLElement) {
  const trackRect = track.getBoundingClientRect();
  const dotRect = dot.getBoundingClientRect();

  return {
    left: dotRect.left - trackRect.left + track.scrollLeft,
    top: dotRect.top - trackRect.top + track.scrollTop,
    width: dotRect.width,
    height: dotRect.height,
  };
}

export function scrollElementToChatTop(element: HTMLElement) {
  const container = getChatScrollContainer(element);
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  if (elementRect.top >= containerRect.top - 1) return;

  const top = container.scrollTop + (elementRect.top - containerRect.top) - CARD_SCROLL_MARGIN;
  const previousBehavior = container.style.scrollBehavior;
  container.style.scrollBehavior = 'auto';
  container.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  container.style.scrollBehavior = previousBehavior;
}

export default function WorkflowStepsCard({
  steps,
  sourceText = '',
  markdownComponents,
}: WorkflowStepsCardProps) {
  const copy = useMemo(() => {
    const fromSteps = steps.map(step => `${step.title}\n${step.body}`).join('\n');
    return getWorkflowUiCopy(sourceText.trim() || fromSteps);
  }, [sourceText, steps]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [slime, setSlime] = useState<SlimeBox>({ left: 0, top: 0, width: 32, height: 32, stretch: false });

  const cardRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLElement>(null);
  const dotsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const skipScrollRef = useRef(true);
  const skipSlimeRef = useRef(true);
  const prevIndexRef = useRef(0);
  const slimeTimerRef = useRef<number | undefined>(undefined);

  const lastIndex = steps.length - 1;
  const current = steps[currentIndex] ?? steps[0];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === lastIndex;
  const titleId = 'aida-workflow-title';

  useEffect(() => {
    setCurrentIndex(index => Math.min(index, Math.max(0, lastIndex)));
  }, [lastIndex, steps.length]);

  useLayoutEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }

    const card = cardRef.current;
    if (!card) return;
    scrollElementToChatTop(card);
  }, [currentIndex, showAll]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    const target = dotsRef.current[currentIndex];
    if (!track || !target || showAll) return;

    const to = measureDotBox(target, track);
    const previousIndex = prevIndexRef.current;
    prevIndexRef.current = currentIndex;

    if (skipSlimeRef.current || previousIndex === currentIndex) {
      skipSlimeRef.current = false;
      setSlime({ ...to, stretch: false });
      return;
    }

    const fromDot = dotsRef.current[previousIndex];
    window.clearTimeout(slimeTimerRef.current);

    if (!fromDot || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setSlime({ ...to, stretch: false });
      return;
    }

    const from = measureDotBox(fromDot, track);
    const sameRow = Math.abs(from.top - to.top) < 8;

    if (!sameRow) {
      setSlime({ ...to, stretch: false });
      return;
    }

    const stretch: SlimeBox = {
      left: Math.min(from.left, to.left),
      top: Math.min(from.top, to.top) + 3,
      width: Math.abs(to.left - from.left) + to.width,
      height: Math.max(20, to.height - 6),
      stretch: true,
    };

    setSlime(stretch);
    slimeTimerRef.current = window.setTimeout(() => {
      setSlime({ ...to, stretch: false });
    }, SLIME_STRETCH_MS);

    return () => window.clearTimeout(slimeTimerRef.current);
  }, [currentIndex, showAll, steps.length]);

  if (!current) return null;

  const slimeStyle: CSSProperties = {
    transform: `translate(${slime.left}px, ${slime.top}px)`,
    width: slime.width,
    height: slime.height,
  };

  const goToStep = (index: number) => {
    setShowAll(false);
    setCurrentIndex(index);
  };

  return (
    <section
      ref={cardRef}
      className={`aida-workflow${showAll ? ' aida-workflow--all' : ''}`}
      aria-labelledby={titleId}
      data-testid="aida-workflow-card"
    >
      {showAll ? (
        <>
          <h2 id={titleId} className="aida-workflow__title">
            {copy.allSteps}
          </h2>
          <ol className="aida-workflow__all">
            {steps.map(step => (
              <li key={`${step.number}-${step.title}`} className="aida-workflow__all-item">
                <h3 className="aida-workflow__all-title">
                  <span className="aida-workflow__all-number">{step.number}</span>
                  {step.title}
                </h3>
                {step.body.trim() ? (
                  <div className="aida-workflow__body">
                    <MarkdownWithMath content={step.body} markdownComponents={markdownComponents} />
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        </>
      ) : (
        <>
          <h2 id={titleId} className="aida-workflow__title">
            {current.title}
          </h2>
          {current.body.trim() ? (
            <div className="aida-workflow__body" aria-live="polite">
              <MarkdownWithMath content={current.body} markdownComponents={markdownComponents} />
            </div>
          ) : null}
        </>
      )}

      <div className="aida-workflow__footer">
        <nav ref={trackRef} className="aida-workflow__dots" aria-label={copy.stepsNav}>
          <span
            className={`aida-workflow__slime${slime.stretch ? ' aida-workflow__slime--stretch' : ''}${
              showAll ? ' aida-workflow__slime--hidden' : ''
            }`}
            style={slimeStyle}
            aria-hidden
          />
          {steps.map((step, index) => {
            const isActive = !showAll && index === currentIndex;

            return (
              <button
                key={`${step.number}-${step.title}`}
                ref={node => {
                  dotsRef.current[index] = node;
                }}
                type="button"
                className={`aida-workflow__dot${isActive ? ' aida-workflow__dot--active' : ''}`}
                aria-label={copy.stepLabel(step.number, step.title)}
                aria-current={isActive ? 'step' : undefined}
                onClick={() => goToStep(index)}
              >
                {step.number}
              </button>
            );
          })}
        </nav>

        <div className="aida-workflow__actions">
          {showAll ? (
            <button
              type="button"
              className="aida-workflow__btn aida-workflow__btn--back"
              onClick={() => setShowAll(false)}
            >
              {copy.stepByStep}
            </button>
          ) : isFirst ? (
            <button
              type="button"
              className="aida-workflow__btn aida-workflow__btn--back"
              onClick={() => setShowAll(true)}
            >
              {copy.allSteps}
            </button>
          ) : (
            <button
              type="button"
              className="aida-workflow__btn aida-workflow__btn--back"
              onClick={() => setCurrentIndex(index => Math.max(0, index - 1))}
            >
              {copy.back}
            </button>
          )}

          {showAll ? null : isLast ? (
            <button
              type="button"
              className="aida-workflow__btn aida-workflow__btn--next"
              onClick={() => goToStep(0)}
            >
              {copy.startOver}
            </button>
          ) : (
            <button
              type="button"
              className="aida-workflow__btn aida-workflow__btn--next"
              onClick={() => setCurrentIndex(index => Math.min(lastIndex, index + 1))}
            >
              {copy.next}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
